'use client';
import { useEffect, useRef, useState } from 'react';
import { Box } from '@mui/material';

import { ComponentErrorBoundary } from '@/components';
import { CommentSkeleton } from '@/components/skeletons/common';
import {
  CommentControls,
  CommentItem,
  CommentList,
  EmptyCommentsState,
} from '@/components/specific/comments';
import { useSportsCommentsInfinite } from '@/services/query';
import type { CommentData } from '@/types';

import { COMMENTS_DEFAULT_SORT } from '@/constants';

export const SportsCommentsTab = ({
  eventId,
  scrollContainerRef,
}: {
  eventId: string | number;
  scrollContainerRef?: React.RefObject<HTMLDivElement>;
}) => {
  const [commentText, setCommentText] = useState('');
  const [sortBy, setSortBy] = useState<string>(COMMENTS_DEFAULT_SORT);
  const [showHolders, setShowHolders] = useState(false);

  const observerTarget = useRef<HTMLDivElement>(null);

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading, isError, refetch } =
    useSportsCommentsInfinite(eventId, sortBy, showHolders);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { threshold: 1.0 }
    );

    if (observerTarget.current) observer.observe(observerTarget.current);
    return () => {
      if (observerTarget.current) observer.unobserve(observerTarget.current);
    };
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const handlePost = () => {
    setCommentText('');
  };

  const renderCommentItem = (comment: CommentData) => (
    <CommentItem key={comment.id} comment={comment} />
  );
  const renderCommentSkeleton = (i: number) => <CommentSkeleton key={`skeleton-${i}`} />;
  const renderLoadingSkeleton = () => <CommentSkeleton />;

  return (
    <ComponentErrorBoundary>
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', position: 'relative' }}>
        <CommentControls
          sortBy={sortBy}
          onSortChange={setSortBy}
          showHolders={showHolders}
          onHoldersToggle={setShowHolders}
        />
        <Box ref={scrollContainerRef} sx={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
          <CommentList
            data={data}
            isLoading={isLoading}
            isError={isError}
            isFetchingNextPage={isFetchingNextPage}
            observerTarget={observerTarget}
            onRetry={refetch}
            renderItem={renderCommentItem}
            renderSkeleton={renderCommentSkeleton}
            renderLoadingMoreSkeleton={renderLoadingSkeleton}
            EmptyState={EmptyCommentsState}
          />
        </Box>
      </Box>
    </ComponentErrorBoundary>
  );
};
